/**
 * SessionTrees.js - Session Collection and Tree Management Component
 *
 * Manages and displays multiple tutoring and classroom session tree visualizations in a unified interface.
 *
 * Features include:
 * - Fetches and displays current session and historical session trees
 * - Responsive GRID layout: the stage measures its own width, picks a column
 *   count, and shrinks every tree by a single shared scale factor so all cards
 *   stay identical and nothing overflows
 * - Numbered PAGINATION: one page is a whole number of rows (5 cols x 2 rows
 *   = 10 on desktop, 1 col x 10 "rows" = 10 on mobile), so a page never ends
 *   mid-row and the same paging UI works at every breakpoint
 * - Auto-jump to the page containing `highlightedTree`: only the current page
 *   is in the DOM, so a tree selected from elsewhere (e.g. Manage Trees) must
 *   have its page shown before anything can find or capture it.
 * - Uniform tree height synchronization across real session trees (via a
 *   shared maxTreeHeight). Simulated trees render at their own natural
 *   max-case size instead of being forced to that value — the grid uses
 *   CSS alignItems:'stretch' so every card's outer box still matches the
 *   tallest card in its row, without needing to inflate or clip any
 *   individual tree's drawn size to hit a computed number.
 * - Session date sorting (newest/oldest first)
 * - Interactive tree selection for comparison and analysis
 * - Simulated (Tree Simulator) trees are merged into the same list, loaded
 *   from a browser cookie (see utils/simulatedTreeStorage) and rendered from
 *   their stored slotMap/totalLeaves inputs rather than transcript analysis.
 *   They're visually called out the same way sample sessions are: a distinct
 *   card background plus a "SIMULATED" watermark behind the tree, so they're
 *   never mistaken for real classroom data.
 * - On the student engagement page, the legend and an optional "Manage Trees"
 *   link (onManageTrees) sit inside the bordered library box, under the cards.
 */

import React, { useEffect, useRef, useState } from "react";
import Tree from "./Tree";
import FlowerTable from "./components/ThreeFlowers";
import SimulatedTalkTreePreview from "./SimulatedTalkTreePreview";
import {
  calculateTreeDimensions,
  calculateRequiredBranches,
  TREE_SCALE,
  DEV_MAX_BRANCH_COUNT,
  DEV_MAX_TRUNK_HEIGHT,
  hasDemoFeaturesParam
} from "./constants/constants";
import { ToggleButton, Box, Typography, ThemeProvider } from '@mui/material';
import { processTreeDataForCounts } from "./components/treeDataProcessor";
import { getTalkMoveTypeFlowerThresholds } from "./utils/treeUtils";
import { getSimulatedTrees, subscribeToSimulatedTrees } from "./utils/simulatedTreeStorage";
import { appTheme } from './constants/theme';


const NATIVE_TREE_WIDTH = 450 * TREE_SCALE;
const GRID_GAP = 16;         
const CARD_PADDING = 10;     
const MIN_COLUMN_WIDTH = 230;
const MAX_COLUMNS = 5;
const PAGE_ROWS = 2;
const MOBILE_PAGE_SIZE = 10;

const SIMULATED_LABEL_COLOR = 'COLORS.Primary';

function SimulatedTreeCard({ tree, isSelected, isHighlighted, onSelect, label, treeScale }) {
  const formatSimDate = (ts) =>
    new Date(ts).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div
      className="tree-stage__item"
      data-tree-id={tree.id}
      data-pill-id={tree.id}
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(tree.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(tree.id); } }}
      style={{
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        padding: `${CARD_PADDING}px`,
        borderRadius: '0.625rem',
        boxShadow: '0px 0.25rem 0.375rem rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        height: '100%',
        cursor: 'pointer',
        outline: isSelected ? '3px solid #4CAF50' : isHighlighted ? '3px solid #1F72E7' : 'none',
        transition: 'outline 0.15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-block', fontSize: '0.875rem', fontWeight: 400, color: '#000',
          padding: '0.3125rem 0.625rem', backgroundColor: '#fff', borderRadius: '0.3125rem',
          whiteSpace: 'nowrap',
        }}>
          {formatSimDate(tree.createdAt)}
        </span>
        {label && (
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: SIMULATED_LABEL_COLOR, whiteSpace: 'nowrap' }}>
            {label}
          </span>
        )}
      </div>

      <div style={{ alignSelf: 'stretch', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
        <SimulatedTalkTreePreview
          totalLeaves={tree.totalLeaves}
          slotMap={tree.slotMap}
          sessionId={tree.id}
          showLegend={false}
          scale={treeScale}
          watermarkText="SIMULATED"
        />
      </div>

      <Box
        sx={{
          boxSizing: 'border-box',
          margin: '0.8rem 0',
          borderRadius: '0.75rem',
          padding: '0.5rem 0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          backgroundColor: '#fff',
        }}
      >
        <Typography
          component="h4"
          sx={{
            fontWeight: 400,
            fontSize: '0.9rem',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {tree.title}
        </Typography>
      </Box>
    </div>
  );
}
export function SortToggle({ sortOrder, onToggle }) {
  const styles = {
    toggleWrapper: { display: 'flex', alignItems: 'center', backgroundColor: '#f0f0f0', borderRadius: '1.25rem', padding: '0.25rem', position: 'relative', cursor: 'pointer', userSelect: 'none', width: 'auto', minWidth: '14rem', height: '2.25rem', outline: 'none' },
    toggleOption: { flex: '1', textAlign: 'center', padding: '0.375rem 1rem', zIndex: 1, color: '#777', transition: 'color 0.3s ease', fontSize: '0.875rem', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', whiteSpace: 'nowrap' },
    toggleOptionActive: { color: '#fff' },
    toggleSlider: { position: 'absolute', top: '0.25rem', left: '0.25rem', width: 'calc(50% - 0.25rem)', height: 'calc(100% - 0.5rem)', backgroundColor: '#92918D', borderRadius: '1rem', transition: 'transform 0.3s ease' },
    sliderRight: { transform: 'translateX(100%)' },
  };
  return (
    <div
      style={styles.toggleWrapper}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      tabIndex={0}
      role="button"
      aria-label={`Sort order: ${sortOrder === 'ascending' ? 'Oldest First' : 'Newest First'}. Press Enter or Space to toggle.`}
      onFocus={(e) => (e.currentTarget.style.boxShadow = '0 0 0 0.125rem #92918D')}
      onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
    >
      <span style={{ ...styles.toggleOption, ...(sortOrder === 'ascending' ? styles.toggleOptionActive : {}) }}>
        <span>↑</span> Oldest First
      </span>
      <span style={{ ...styles.toggleOption, ...(sortOrder === 'descending' ? styles.toggleOptionActive : {}) }}>
        <span>↓</span> Newest First
      </span>
      <div style={{ ...styles.toggleSlider, ...(sortOrder === 'descending' ? styles.sliderRight : {}) }} />
    </div>
  );
}
const SessionTrees = ({
  processTranscriptData,
  analyticsSessionID,
  previousSessionIds = [],
  retrieveSagaUserRegion,
  inputType,
  region,
  isTutorFeedbackPage,
  isStudentEngagementPage,
  showSessionDetails = false,
  client,
  onTreeHover,
  onTreeLeave,
  selectedTrees = new Set(),
  onTreeSelect,
  selectionMode,
  label = "My Student Talk Trees",
  showDescriptionOnly = false,
  highlightedTree = null,
  treeLabels = {},
  showTutorToggle = false,
  showSpeakerLabels = false,
  showTalkMoveStats = false,
  showSimulatedTrees = true,
  sortOrder: sortOrderProp, 
  onToggleSort,             
  showHeader = true, 
  onManageTrees,
}) => {
  const validPreviousSessionIds = Array.isArray(previousSessionIds) ? previousSessionIds : [];

  const [sessionsData, setSessionsData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [internalSortOrder, setInternalSortOrder] = useState('descending');
const sortOrder = sortOrderProp ?? internalSortOrder;
  const [availableSessions, setAvailableSessions] = useState([]);
  const [showTutorVisualization, setShowTutorVisualization] = useState(false);
  const [maxTreeHeight, setMaxTreeHeight] = useState(500);
  const [sessionHeights, setSessionHeights] = useState({});
  const [isDataFullyLoaded, setIsDataFullyLoaded] = useState(false);
  const [simulatedTrees, setSimulatedTrees] = useState([]);

  const stageRef = useRef(null);
  const [stageWidth, setStageWidth] = useState(0);

  // Current 1-based page. One page is always a whole number of grid rows,
  // so pagination reads the same way on a 1-column mobile stack as it does
  // on a 5-column desktop grid — no separate "Load more" mode.
  const [currentPage, setCurrentPage] = useState(1);
  const jumpedForHighlightRef = useRef(null);

  useEffect(() => {
    setSessionsData({});
    setError(null);
    setIsDataFullyLoaded(false);

    if (!analyticsSessionID && (!validPreviousSessionIds.length || !validPreviousSessionIds.some(id => id))) {
      setIsLoading(false);
      setAvailableSessions([]);
      setIsDataFullyLoaded(true);
      return;
    }

    setIsLoading(true);

    const fetchAllSessionsData = async () => {
      const allSessionIds = [analyticsSessionID, ...validPreviousSessionIds].filter(Boolean);
      const validatedSessionIds = [];

      try {
        const sessionsDataObj = {};

        for (const sessionId of allSessionIds) {
          if (!sessionId) continue;
          try {
            const data = await processTranscriptData(sessionId, inputType);
            if (data) {
              sessionsDataObj[sessionId] = data;
              validatedSessionIds.push(sessionId);
            }
          } catch (sessionError) {
            console.error(`Error processing session ${sessionId}:`, sessionError);
          }
        }

        setSessionsData(sessionsDataObj);
        setAvailableSessions(validatedSessionIds);

        const showDemoFeatures = hasDemoFeaturesParam();
        const branchCountOverride = showDemoFeatures ? DEV_MAX_BRANCH_COUNT : undefined;
        const trunkHeightOverride = showDemoFeatures ? DEV_MAX_TRUNK_HEIGHT : undefined;


        let maxBranchCount = 5;
        const heightsBySession = {};
        validatedSessionIds.forEach(sessionId => {
          const sessionData = sessionsDataObj[sessionId];
          if (sessionData?.processedData) {
            const treeData = processTreeDataForCounts(sessionData.processedData, 'student');
            const branchInfo = calculateRequiredBranches(treeData.totalLeaves, treeData.totalFlowers, branchCountOverride);
            maxBranchCount = Math.max(maxBranchCount, branchInfo.branchCount);

            if (showDemoFeatures) {
              const sessionDimensions = calculateTreeDimensions(branchInfo.branchCount, trunkHeightOverride);
              heightsBySession[sessionId] = sessionDimensions.backgroundHeight * TREE_SCALE;
            }
          }
        });

        const maxDimensions = calculateTreeDimensions(maxBranchCount, trunkHeightOverride);
        setMaxTreeHeight(maxDimensions.backgroundHeight * TREE_SCALE);
        if (showDemoFeatures) setSessionHeights(heightsBySession);
        setIsDataFullyLoaded(true);
      } catch (err) {
        setError("Error fetching sessions data: " + err.message);
        console.error("Error fetching sessions data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllSessionsData();
  }, [analyticsSessionID, validPreviousSessionIds, processTranscriptData, inputType]);

  // Simulated trees live in a cookie (see utils/simulatedTreeStorage), not
  // the backend, so they're loaded and refreshed independently of the fetch
  // above. Subscribing means a tree saved from the Tree Simulator elsewhere
  // on the page shows up here right away.
  useEffect(() => {
    if (!showSimulatedTrees) {
      setSimulatedTrees([]);
      return;
    }
    const loadSimulatedTrees = () => setSimulatedTrees(getSimulatedTrees());
    loadSimulatedTrees();
    const unsubscribe = subscribeToSimulatedTrees(loadSimulatedTrees);
    return unsubscribe;
  }, [showSimulatedTrees]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => setStageWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isDataFullyLoaded, isLoading, error]);

  const handleTutorVizToggle = () => setShowTutorVisualization(prev => !prev);
  const toggleSortOrder = onToggleSort ?? (() => setInternalSortOrder(prev => (prev === 'ascending' ? 'descending' : 'ascending')));

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getSortedSessions = () => {
    const real = availableSessions.map(sessionId => ({
      sessionId,
      timestamp: sessionsData[sessionId]?.sessionMetadata?.startTime || 0,
      isCurrentSession: sessionId === analyticsSessionID,
    }));
    const simulated = simulatedTrees.map(tree => ({
      sessionId: tree.id,
      timestamp: tree.createdAt,
      isCurrentSession: false,
    }));
    const combined = [...real, ...simulated];
    if (sortOrder === 'ascending') combined.sort((a, b) => a.timestamp - b.timestamp);
    else combined.sort((a, b) => b.timestamp - a.timestamp);
    return combined;
  };

  const sortedSessions = getSortedSessions();

  const idsToRender = sortedSessions.map(s => s.sessionId);
  const selectedInOrder = idsToRender.filter(id => selectedTrees?.has(id));

  const simulatedById = Object.fromEntries(simulatedTrees.map(t => [t.id, t]));


  const columns = stageWidth > 0
    ? Math.max(1, Math.min(MAX_COLUMNS, Math.floor((stageWidth + GRID_GAP) / (MIN_COLUMN_WIDTH + GRID_GAP))))
    : 1;
  const columnWidth = stageWidth > 0
    ? (stageWidth - GRID_GAP * (columns - 1)) / columns
    : NATIVE_TREE_WIDTH;
  const treeScale = Math.min(1, Math.max(0.2, (columnWidth - CARD_PADDING * 2) / NATIVE_TREE_WIDTH));


  // ── Pagination ──────────────────────────────────────────────────────────
  // One page is always a whole number of rows: on a multi-column desktop
  // grid that's columns x PAGE_ROWS; on the 1-column mobile stack it's just
  // MOBILE_PAGE_SIZE cards. Either way it's the same numbered-page UI below.
  const pageSize = columns === 1 ? MOBILE_PAGE_SIZE : columns * PAGE_ROWS;
  const totalCount = idsToRender.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Keep the page in range: both the list length and the column count can
  // change under the user (deletes, resize), and a page that no longer
  // exists would render an empty grid.
  useEffect(() => {
    setCurrentPage(p => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const visibleIds = idsToRender.slice(pageStart, pageStart + pageSize);
  const renderColumns = showTalkMoveStats
  ? Math.max(1, Math.min(columns, visibleIds.length))
  : columns;
  const showDemoFeatures = hasDemoFeaturesParam();
  const rowHeightBySession = {};
  if (showDemoFeatures) {
    const rowSize = Math.max(1, renderColumns);
    for (let i = 0; i < visibleIds.length; i += rowSize) {
      const rowIds = visibleIds.slice(i, i + rowSize);
      const rowHeights = rowIds
        .map(id => sessionHeights[id])
        .filter(h => typeof h === 'number' && h > 0);
      const rowHeight = rowHeights.length ? Math.max(...rowHeights) : maxTreeHeight;
      rowIds.forEach(id => { rowHeightBySession[id] = rowHeight; });
    }
  } else {
    visibleIds.forEach(id => { rowHeightBySession[id] = maxTreeHeight; });
  }

  // Windowed page numbers with ellipses, e.g. 1 … 4 5 6 … 12
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    const from = Math.max(2, safePage - 1);
    const to = Math.min(totalPages - 1, safePage + 1);
    if (from > 2) pages.push('…');
    for (let i = from; i <= to; i++) pages.push(i);
    if (to < totalPages - 1) pages.push('…');
    pages.push(totalPages);
    return pages;
  };

  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next === safePage) return;
    setCurrentPage(next);
    // Bring the top of the grid back into view after a page change.
    if (stageRef.current) stageRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  useEffect(() => {
    if (!highlightedTree) {
      jumpedForHighlightRef.current = null;
      return;
    }
    if (jumpedForHighlightRef.current === highlightedTree) return;
    if (!isDataFullyLoaded || stageWidth <= 0) return;

    const idx = idsToRender.indexOf(highlightedTree);
    if (idx === -1) return; 
    jumpedForHighlightRef.current = highlightedTree;
    setCurrentPage(Math.floor(idx / pageSize) + 1);
  }, [highlightedTree, isDataFullyLoaded, stageWidth, pageSize, idsToRender.join('|')]);

  useEffect(() => {
    if (!highlightedTree) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-pill-id="${highlightedTree}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 300);
    return () => clearTimeout(t);
  }, [highlightedTree, isDataFullyLoaded, safePage]);

  useEffect(() => {
    if (!selectedInOrder.length) return;
    const el = document.querySelector(`[data-pill-id="${selectedInOrder[0]}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [sortOrder, JSON.stringify(selectedInOrder)]);

  const isSampleTitle = (title) => {
    const t = title?.toLowerCase() ?? '';
    return t.startsWith('sample session') || t.startsWith('sample tree');
  };

  const styles = {
    sessionItem: {
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      padding: `${CARD_PADDING}px`,
      borderRadius: '0.625rem',
      backgroundColor: '#FDFCFA',
      boxShadow: '0px 0.25rem 0.375rem rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'flex-start',
    },
    sessionDate: {
      display: 'inline-block',
      fontSize: '0.875rem',
      fontWeight: 400,
      color: '#000',
      padding: '0.3125rem 0.625rem',
      backgroundColor: '#f5f5f5',
      borderRadius: '0.3125rem',
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },
    toggleWrapper: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#f0f0f0',
      borderRadius: '1.25rem',
      padding: '0.25rem',
      position: 'relative',
      cursor: 'pointer',
      userSelect: 'none',
      width: 'auto',
      minWidth: '14rem',
      height: '2.25rem',
      outline: 'none',
    },
    toggleOption: {
      flex: '1',
      textAlign: 'center',
      padding: '0.375rem 1rem',
      zIndex: 1,
      color: '#777',
      transition: 'color 0.3s ease',
      fontSize: '0.875rem',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      whiteSpace: 'nowrap',
    },
    toggleOptionActive: { color: '#fff' },
    toggleSlider: {
      position: 'absolute',
      top: '0.25rem',
      left: '0.25rem',
      width: 'calc(50% - 0.25rem)',
      height: 'calc(100% - 0.5rem)',
      backgroundColor: '#92918D',
      borderRadius: '1rem',
      transition: 'transform 0.3s ease',
    },
    sliderRight: { transform: 'translateX(100%)' },
    noSessionsMessage: {
      textAlign: 'center',
      padding: '1.25rem',
      color: '#666',
      fontStyle: 'italic',
      fontSize: '0.875rem',
    },
    pageBtn: {
      minWidth: '32px',
      height: '32px',
      padding: '0 8px',
      borderRadius: '6px',
      border: '1px solid transparent',
      background: 'transparent',
      color: '#374151',
      fontFamily: 'inherit',
      fontSize: '14px',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.15s ease, color 0.15s ease',
    },
  };

  const formatTalkMoveType = (type) =>
    type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const computeTalkMoveCounts = (processedData, speakerType) =>
    (processedData || [])
      .filter(item => item.speakerType?.toLowerCase() === speakerType.toLowerCase())
      .reduce((acc, item) => {
        if (item.talkMoveType && item.talkMoveType.toLowerCase() !== 'none')
          acc[item.talkMoveType] = (acc[item.talkMoveType] || 0) + 1;
        return acc;
      }, {});

  const makeTable = (counts, activeTypes) => (
    <div style={{ padding: '8px 16px', minWidth: '160px', flexShrink: 0, alignSelf: 'center' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, backgroundColor: '#f0f0f0' }}>Talk Move</th>
            <th style={{ textAlign: 'center', padding: '4px 8px', fontWeight: 600, backgroundColor: '#f0f0f0' }}>Count</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(counts).sort().map(type => {
            const isActive = activeTypes.has(type);
            return (
              <tr key={type}>
                <td style={{ padding: '2px 8px', fontWeight: isActive ? 700 : 400, textAlign: 'left' }}>
                  {formatTalkMoveType(type)}
                </td>
                <td style={{ textAlign: 'center', padding: '2px 8px', fontWeight: isActive ? 700 : 400 }}>
                  {counts[type]}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderTree = (sessionId, rowMaxHeight = maxTreeHeight) => {
    const processedData = sessionsData[sessionId]?.processedData;
    const studentCounts = showTalkMoveStats ? computeTalkMoveCounts(processedData, 'student') : null;
    const tutorCounts = showTalkMoveStats ? computeTalkMoveCounts(processedData, 'tutor') : null;
    const hasTutorStats = tutorCounts && Object.keys(tutorCounts).length > 0;
    const studentActiveTypes = showTalkMoveStats ? new Set(Object.keys(getTalkMoveTypeFlowerThresholds('student'))) : null;
    const tutorActiveTypes = showTalkMoveStats ? new Set(Object.keys(getTalkMoveTypeFlowerThresholds('tutor'))) : null;

    const studentPanel = (showTalkMoveStats && showTutorVisualization && hasTutorStats)
      ? makeTable(studentCounts, studentActiveTypes) : null;
    const tutorPanel = (showTalkMoveStats && showTutorVisualization && hasTutorStats)
      ? makeTable(tutorCounts, tutorActiveTypes) : null;

    return (
      <Tree
        processTranscriptData={processTranscriptData}
        sessionId={sessionId}
        retrieveSagaUserRegion={retrieveSagaUserRegion}
        yearInputType={inputType}
        region={region}
        isTutorFeedbackPage={isTutorFeedbackPage}
        isStudentEngagementPage={isStudentEngagementPage}
        showSessionDetails={showSessionDetails}
        client={client}
        onTreeHover={onTreeHover}
        onTreeLeave={onTreeLeave}
        isSelected={selectedTrees?.has(sessionId) || false}
        onTreeSelect={onTreeSelect}
        showTutorVisualization={showTutorVisualization}
        showSpeakerLabels={showSpeakerLabels}
        maxTreeHeight={rowMaxHeight}
        showDescriptionOnly={showDescriptionOnly}
        studentSidePanel={studentPanel}
        tutorSidePanel={tutorPanel}
        treeScale={treeScale}
      />
    );
  };

  if (isLoading) return <div>Loading session data...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (sortedSessions.length === 0) return <div style={styles.noSessionsMessage}>No session data available.</div>;
  if (!isDataFullyLoaded || !maxTreeHeight || maxTreeHeight <= 0) return <div>Calculating tree dimensions...</div>;

  const compareMode = selectionMode === 'compare';
  const needsSecondForCompare = compareMode && selectedInOrder.length < 2;

  return (
    <ThemeProvider theme={appTheme}>
      <div className="all-sessions-container">
       {showHeader && (

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '8px', margin: '10px 0px 5px 0px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {label && (
              <span style={{ fontWeight: 600, fontSize: '20px', color: '#111827', whiteSpace: 'nowrap' }}>
                {label}
              </span>
            )}
            {showTutorToggle && (
              <ToggleButton
                value="check"
                selected={showTutorVisualization}
                onChange={handleTutorVizToggle}
                aria-label="toggle tutor visualization"
                size="small"
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#92918D', color: 'white',
                    '&:hover': { backgroundColor: '#7a7976' },
                  },
                }}
              >
                {showTutorVisualization ? 'Hide' : 'Show'} Tutor Visualization
              </ToggleButton>
            )}
          </div>

          <div
            style={styles.toggleWrapper}
            onClick={toggleSortOrder}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSortOrder(); } }}
            tabIndex={0}
            role="button"
            aria-label={`Sort order: ${sortOrder === 'ascending' ? 'Oldest First' : 'Newest First'}. Press Enter or Space to toggle.`}
            onFocus={(e) => (e.currentTarget.style.boxShadow = '0 0 0 0.125rem #92918D')}
            onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
          >
            <span style={{ ...styles.toggleOption, ...(sortOrder === 'ascending' ? styles.toggleOptionActive : {}) }}>
              <span>↑</span> Oldest First
            </span>
            <span style={{ ...styles.toggleOption, ...(sortOrder === 'descending' ? styles.toggleOptionActive : {}) }}>
              <span>↓</span> Newest First
            </span>
            <div style={{ ...styles.toggleSlider, ...(sortOrder === 'descending' ? styles.sliderRight : {}) }} />
          </div>
        </div>
       )}

        <div style={{ border: '1px solid #B0AFAD', borderRadius: '8px', padding: '16px', marginTop: '10px' }}>

          {needsSecondForCompare && (
            <div style={{ fontSize: '13px', color: '#1F72E7', margin: '4px 2px 12px' }} role="status">
              {selectedInOrder.length === 0
                ? 'Select two dates above to compare their trees.'
                : 'Select one more date to compare.'}
            </div>
          )}

          {/* ---- The stage: responsive grid of tree cards ---- */}
          <div
            ref={stageRef}
            className={`tree-stage${compareMode ? ' tree-stage--compare' : ''}`}
            style={{
              display: 'grid',
              gridTemplateColumns: showTalkMoveStats
                ? `repeat(${renderColumns}, max-content)`
                : `repeat(${columns}, minmax(0, 1fr))`,
              justifyContent: showTalkMoveStats ? 'flex-start' : 'normal',
              gap: `${GRID_GAP}px`,
              alignItems: 'stretch',
            }}
          >
              {visibleIds.map((sessionId) => {
              const simTree = simulatedById[sessionId];
              if (simTree) {
                return (
                  <SimulatedTreeCard
                    key={sessionId}
                    tree={simTree}
                    isSelected={selectedTrees?.has(sessionId) || false}
                    isHighlighted={highlightedTree === sessionId}
                    onSelect={onTreeSelect}
                    label={treeLabels[sessionId]}
                    maxTreeHeight={rowHeightBySession[sessionId] ?? maxTreeHeight}
                    treeScale={treeScale}
                  />
                );
              }

              const meta = sessionsData[sessionId]?.sessionMetadata;
              const isSample = isSampleTitle(meta?.title);
              return (
                <div
                  key={sessionId}
                  className="tree-stage__item"
                  data-tree-id={sessionId}
                  data-pill-id={sessionId}
                  style={{
                    ...styles.sessionItem,
                    backgroundColor: isSample ? '#E8EEF7' : '#FDFCFA',
                    outline: selectedTrees?.has(sessionId) ? '3px solid #4CAF50' : 'none',
                    transition: 'outline 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem', alignItems: 'center', gap: '8px' }}>
                    <span style={styles.sessionDate}>
                      {formatDisplayDate(new Date(meta?.startTime))}
                    </span>
                    {treeLabels[sessionId] && (
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1F72E7', whiteSpace: 'nowrap' }}>
                        {treeLabels[sessionId]}
                      </span>
                    )}
                  </div>

                  <div style={{ alignSelf: 'stretch', minWidth: 0}}>
                    {renderTree(sessionId, rowHeightBySession[sessionId])}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ---- Numbered pagination (mobile and desktop alike) ---- */}
          {totalPages > 1 && (
            <nav
              aria-label="Talk tree pages"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                marginTop: '24px',
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
                aria-label="Previous page"
                style={{
                  ...styles.pageBtn,
                  color: safePage === 1 ? '#D1D5DB' : '#374151',
                  cursor: safePage === 1 ? 'default' : 'pointer',
                }}
              >
                ‹
              </button>

              {getPageNumbers().map((p, i) =>
                p === '…' ? (
                  <span
                    key={`gap-${i}`}
                    aria-hidden="true"
                    style={{ ...styles.pageBtn, cursor: 'default', color: '#9CA3AF' }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => goToPage(p)}
                    aria-label={`Page ${p}`}
                    aria-current={p === safePage ? 'page' : undefined}
                    style={{
                      ...styles.pageBtn,
                      color: p === safePage ? '#1F72E7' : '#374151',
                      fontWeight: p === safePage ? 700 : 400,
                      backgroundColor: p === safePage ? '#EFF5FE' : 'transparent',
                      borderColor: p === safePage ? '#1F72E7' : 'transparent',
                    }}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
                aria-label="Next page"
                style={{
                  ...styles.pageBtn,
                  color: safePage === totalPages ? '#D1D5DB' : '#374151',
                  cursor: safePage === totalPages ? 'default' : 'pointer',
                }}
              >
                ›
              </button>
            </nav>
          )}

          {totalCount > pageSize && (
            <div
              role="status"
              aria-live="polite"
              style={{ textAlign: 'center', fontSize: '12px', color: '#6B7280', marginTop: '8px' }}
            >
              {`Showing ${pageStart + 1}–${pageStart + visibleIds.length} of ${totalCount} trees`}
            </div>
          )}

          {/* ---- Legend row inside the library box: legend on the left, Manage Trees on the right ---- */}
          {isStudentEngagementPage && (
            <div
              style={{
                marginTop: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'nowrap', minWidth: 0 }}>
                <span style={{ fontSize: '14px', color: '#4B5563', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  Legend
                </span>
                <FlowerTable isPresentationPage={false} />
              </div>

              {onManageTrees && (
                <button
                  type="button"
                  onClick={onManageTrees}
                  aria-label="Manage trees"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#1F72E7',
                    fontFamily: 'inherit',
                    fontSize: '15px',
                    padding: '4px 8px',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                  </svg>
                  Manage Trees
                </button>
              )}
            </div>
          )}
        </div>

        <style>{`
          /* Grid columns/gap are set inline from the measured width — this block
             only carries the bits that don't depend on measurement. */
          .tree-stage__item { min-width: 0; }
        `}</style>
      </div>
    </ThemeProvider>
  );
};

export default SessionTrees;